///
/// EarthBrowser
///
/// Created by Matt Giger on 10/25/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Local copy of the historical catalog (USGS ComCat M≥4.5 since 1900,
/// ~300k rows, published by tools/quake_history). Table-only: nothing is
/// drawn from it directly; the earthquake cutout reads it for "has this
/// happened here before?" and the ghost hypocenters.
///

import { Database } from "@/earthbrowser/database/Database"

const cBatchSize = 2000

export interface QuakeHistoryRow {
	id: string
	/** Epoch milliseconds */
	time_ms: number
	lat: number
	lon: number
	depth_km: number
	mag: number
	mag_type: string
}

export class QuakeHistoryTable {
	public static async create(db: Database): Promise<void> {
		// Plain lat/lon columns with a latitude B-tree instead of a geometry
		// R-tree: queries are small boxes around one point, and skipping the
		// per-row geometry construction keeps the 300k-row ingest quick
		await db.bulkExec([
			{
				sql: `create table if not exists earthquake_history (
					id text primary key,
					time_ms real not null,
					lat real not null,
					lon real not null,
					depth_km real not null,
					mag real not null,
					mag_type text
				)`
			},
			{ sql: "create index if not exists idx_earthquake_history_lat on earthquake_history(lat, lon)" },
			{ sql: "create index if not exists idx_earthquake_history_mag on earthquake_history(mag desc)" },
		])
	}

	public static async replaceAll(rows: QuakeHistoryRow[]): Promise<void> {
		await Database.db.exec("delete from earthquake_history", [], "low")
		const statement = `insert or replace into earthquake_history (id, time_ms, lat, lon, depth_km, mag, mag_type)
			values (?, ?, ?, ?, ?, ?, ?)`
		for (let i = 0; i < rows.length; i += cBatchSize) {
			const batch = rows.slice(i, i + cBatchSize)
			await Database.db.bulkInsert(statement, batch.map(r => [
				r.id, r.time_ms, r.lat, r.lon, r.depth_km, r.mag, r.mag_type
			]), "low")
		}
	}

	public static async getCount(): Promise<number> {
		const rows = await Database.db.exec("select count(*) as count from earthquake_history", [], "low") as Array<{ count: number }>
		return rows[0]?.count ?? 0
	}

	/** Rows in a lat/lon box (degrees); the caller refines the shape */
	public static async queryBox(minLat: number, maxLat: number, minLon: number, maxLon: number, beforeMs: number): Promise<QuakeHistoryRow[]> {
		// Boxes crossing the antimeridian are rare for a few-hundred-km block; split them
		if (minLon < -180 || maxLon > 180) {
			const a = await this.queryBox(minLat, maxLat, Math.max(-180, minLon), Math.min(180, maxLon), beforeMs)
			const wrapped = minLon < -180
				? await this.queryBox(minLat, maxLat, minLon + 360, 180, beforeMs)
				: await this.queryBox(minLat, maxLat, -180, maxLon - 360, beforeMs)
			return a.concat(wrapped)
		}
		return await Database.db.exec(
			`select id, time_ms, lat, lon, depth_km, mag, mag_type
			from earthquake_history
			where lat between ? and ? and lon between ? and ? and time_ms < ?
			order by time_ms`,
			[minLat, maxLat, minLon, maxLon, beforeMs],
			"high"
		) as QuakeHistoryRow[]
	}
}
